<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePWAStore } from '@/stores/pwa'
import Button from '@/components/ui/Button.vue'
import { signOut } from 'firebase/auth'
import createLogger from '@/utils/logger'
import { auth } from '@/firebase'
import { useZoomJoinAnyTimeDebug } from '@/composables/useZoomJoinAnyTimeDebug'
import { useNotificationsStore } from '@/stores/notifications'
import { useAuthStore } from '@/stores/auth'
import { useCustomerStore } from '@/stores/customer'
import { Admin } from '@/components/cloudFunctions/admin'
import { useToastStore } from '@/stores/toast'
import type { CustomerId } from '@gh/common/models'
import { DEVELOPER_ACCOUNT_MESSAGE } from '@/constants/messages'

import { canvasApi } from '@/components/cloudFunctions/canvasMedical'

const route = useRoute()
const router = useRouter()
const isAdminPanel = computed(() => route.path.startsWith('/admin'))

const logger = createLogger('DevToolsManagerActions.vue')
const pwaStore = usePWAStore()
const authStore = useAuthStore()
const customerStore = useCustomerStore()
const toastStore = useToastStore()
const { zoomJoinAnyTimeEnabled: zoomJoinAnyTime, toggleZoomJoinAnyTime } = useZoomJoinAnyTimeDebug()
const notificationsStore = useNotificationsStore()
const isDeveloperAccountToggling = ref(false)
const isPluginLogsSyncing = ref(false)

const developerAccount = computed(() => customerStore.customer?.developerAccount ?? false)

const showDeveloperAccountToggle = computed(() => Boolean(authStore.user) && authStore.isAdmin)
function handleToggleZoomJoinAnyTime() {
  toggleZoomJoinAnyTime()
  logger.log('Zoom join any time:', zoomJoinAnyTime.value ? 'enabled' : 'disabled')
}

const handleTestSignOffAndClearStorage = async () => {
  try {
    // Firebase Auth signout
    await signOut(auth)

    // Cleanup signup store
    // signupStore.cleanup()

    const loggerConfig = localStorage.getItem('logger_config')
    const perClassScheduleConfigVisible = localStorage.getItem('perClassScheduleConfigVisible')

    // Clear localStorage
    localStorage.clear()

    if (loggerConfig) {
      localStorage.setItem('logger_config', loggerConfig)
    }

    if (perClassScheduleConfigVisible) {
      localStorage.setItem('perClassScheduleConfigVisible', perClassScheduleConfigVisible)
    }

    // Clear sessionStorage
    sessionStorage.clear()

    // Navigate to login
    // await router.push('/login')
    window.location.href = '/login'

    logger.log('Test signoff completed successfully')
  } catch (error) {
    console.error('Error during test signoff:', error)
  }
}

const handleTestSignOff = async () => {
  try {
    // Firebase Auth signout
    await signOut(auth)

    // Navigate to login
    // await router.push('/login')
    window.location.href = '/login'

    logger.log('Test signoff completed successfully')
  } catch (error) {
    console.error('Error during test signoff:', error)
  }
}

const handleTogglePerClassScheduleConfigVisible = () => {
  notificationsStore.setPerClassScheduleConfigVisible(
    !notificationsStore.perClassScheduleConfigVisible,
  )
}

const handleReloadPage = () => {
  window.location.reload()
}

const openClinicalConsultationTest = () => {
  void router.push({
    name: 'admin-clinical-consultation-test-object',
    params: { objectType: 'consultation' },
  })
}

const handleToggleDeveloperAccount = async () => {
  const customerId = authStore.user?.uid as CustomerId | undefined
  if (!customerId || isDeveloperAccountToggling.value) return

  isDeveloperAccountToggling.value = true
  const nextValue = !developerAccount.value
  try {
    const result = await Admin.setCustomerDeveloperAccount({
      customerId,
      developerAccount: nextValue,
    })
    if (result.success) {
      if (customerStore.customer) {
        customerStore.customer.developerAccount = result.developerAccount
      }
      toastStore.success(
        nextValue ? DEVELOPER_ACCOUNT_MESSAGE : 'Developer account flag removed.',
        10,
      )
    } else {
      toastStore.error('Failed to update developer account flag.')
    }
  } catch (error) {
    logger.error('Failed to toggle developer account', error)
    toastStore.error('Failed to update developer account flag.')
  } finally {
    isDeveloperAccountToggling.value = false
  }
}

const handleSyncCanvasPluginLogs = async () => {
  if (!authStore.isAdmin || isPluginLogsSyncing.value) return

  isPluginLogsSyncing.value = true
  try {
    const result = await canvasApi.triggerPluginLogsSync({})
    if (result.success && result.queued) {
      toastStore.success(`Canvas plugin logs sync queued (${result.eventId})`, 10)
    } else {
      toastStore.error('Failed to queue Canvas plugin logs sync.')
    }
  } catch (error) {
    logger.error('Failed to queue Canvas plugin logs sync', error)
    toastStore.error('Failed to queue Canvas plugin logs sync.')
  } finally {
    isPluginLogsSyncing.value = false
  }
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <Button v-if="isAdminPanel" variant="accent" class="flex" @click="openClinicalConsultationTest">
      Clinical Consultation Test
    </Button>
    <Button variant="primary" @click="handleTestSignOff" class="text-white px-4 py-2 rounded flex">
      Sign Off
    </Button>
    <Button
      variant="primary"
      @click="handleTestSignOffAndClearStorage"
      class="text-white px-4 py-2 rounded flex"
    >
      Sign Off & clear storage
    </Button>
    <Button variant="accent" @click="pwaStore.clearPromptDismissed" class="flex"
      >Clear PWA Prompt Dismissed</Button
    >
    <Button variant="accent" @click="handleReloadPage" class="flex">Reload Page</Button>
    <Button
      :variant="zoomJoinAnyTime ? 'primary' : 'accent'"
      @click="handleToggleZoomJoinAnyTime"
      class="flex"
      :title="
        zoomJoinAnyTime
          ? 'Disable to enforce normal join windows (class: 15 min before – end; consult: 15 min before – end)'
          : 'Allow joining Zoom regardless of class or consultation time (takes effect immediately)'
      "
    >
      {{ zoomJoinAnyTime ? 'Join any Zoom: ON' : 'Join any Zoom: OFF' }}
    </Button>
    <Button
      :variant="notificationsStore.perClassScheduleConfigVisible ? 'accent' : 'outline'"
      @click="handleTogglePerClassScheduleConfigVisible"
      class="flex"
    >
      Toggle Per Class Schedule Config Visibility, currently
      {{ notificationsStore.perClassScheduleConfigVisible ? 'ON' : 'OFF' }}
    </Button>
    <Button
      v-if="authStore.isAdmin"
      variant="accent"
      class="flex"
      :disabled="isPluginLogsSyncing"
      @click="handleSyncCanvasPluginLogs"
    >
      Sync canvas plugin logs
    </Button>
    <Button
      v-if="showDeveloperAccountToggle"
      :variant="developerAccount ? 'primary' : 'accent'"
      :disabled="isDeveloperAccountToggling"
      class="flex"
      @click="handleToggleDeveloperAccount"
    >
      {{ developerAccount ? 'Developer account: ON' : 'Developer account: OFF' }}
    </Button>
  </div>
</template>
